import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Palmtree, Calendar, CalendarDays } from 'lucide-react';
import ExportCalendarModal from './ExportCalendarModal';

const EnvDropdown = ({ envEmoji, user, profile, saveUserProfile, isMobile = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        buttonRef.current && !buttonRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) {
        setIsOpen(false);
      }
    };

    const updatePosition = () => {
      if (buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + 8, left: rect.left });
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);
      updatePosition();
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const toggleDropdown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 8, left: rect.left });
    }
    setIsOpen(!isOpen);
  };

  const openExportModal = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowExportModal(true);
    setIsOpen(false);
  };

  const toggleMondayStart = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const isMondayFirst = profile?.mondayFirst || false;
    if (saveUserProfile) {
      await saveUserProfile({ ...profile, mondayFirst: !isMondayFirst });
    }
  };

  const location = useLocation();
  const navigate = useNavigate();
  const isCalendar = location.pathname.includes('/calendar');

  const renderEmoji = () => {
    if (envEmoji === '🌴') {
      return <Palmtree size={isMobile ? 32 : 20} className="text-green-600 flex-shrink-0" strokeWidth={2.5} />;
    }
    return <span className={isMobile ? 'text-3xl' : 'text-lg'}>{envEmoji}</span>;
  };

  const dropdownMenu = isOpen ? (
    <div 
      ref={dropdownRef}
      style={{ top: dropdownPos.top, left: dropdownPos.left }}
      className="fixed w-48 bg-white border border-zinc-200 rounded-2xl shadow-xl z-[9999] overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
    >
      <div className="p-2 flex flex-col gap-1">
        {isCalendar && (
          <>
            <button
              onClick={openExportModal}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50 rounded-xl transition-colors text-zinc-600 hover:text-zinc-900 w-full text-left"
            >
              <Calendar size={16} strokeWidth={2.5} className="text-blue-500" />
              <span className="text-[10px] font-black uppercase tracking-widest">Create Calendar Feed</span>
            </button>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsOpen(false);
                navigate('/settings');
                setTimeout(() => {
                  const el = document.getElementById('exported-calendars-section');
                  if (el) el.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50 rounded-xl transition-colors text-zinc-600 hover:text-zinc-900 w-full text-left"
            >
              <CalendarDays size={16} strokeWidth={2.5} className="text-purple-500" />
              <span className="text-[10px] font-black uppercase tracking-widest">Manage Feeds</span>
            </button>
            <button
              onClick={toggleMondayStart}
              className="flex items-center gap-3 px-3 py-2.5 hover:bg-zinc-50 rounded-xl transition-colors text-zinc-600 hover:text-zinc-900 w-full text-left"
            >
              <CalendarDays size={16} strokeWidth={2.5} className={profile?.mondayFirst ? "text-green-500" : "text-zinc-400"} />
              <span className="text-[10px] font-black uppercase tracking-widest">
                {profile?.mondayFirst ? 'Monday Start: On' : 'Monday Start: Off'}
              </span>
            </button>
          </>
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="relative inline-block">
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        className={`flex items-center justify-center transition-transform active:scale-95 ${isMobile ? '' : 'hover:opacity-70'}`}
      >
        {renderEmoji()}
      </button>

      {isOpen && createPortal(dropdownMenu, document.body)}

      {showExportModal && (
        <ExportCalendarModal
          user={user}
          profile={profile}
          saveUserProfile={saveUserProfile}
          onClose={() => setShowExportModal(false)}
        />
      )}
    </div>
  );
};

export default EnvDropdown;